"use client";

import Link from "next/link";
import type { CSSProperties } from "react";
import { useCallback, useMemo, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  type CollisionDetection,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  DropdownMenu,
  Flex,
  Heading,
  IconButton,
  Select,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import {
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandX,
  IconBrandYoutube,
  IconDotsVertical,
  IconGripVertical,
  IconMessage,
  IconMessageCircle,
  IconSearch,
  IconSend,
} from "@tabler/icons-react";
import {
  budgetLinePrice,
  calculateEngagementBudget,
  capitalize,
  creatorHasSmsPhone,
  getCreatorSmsPhone,
} from "@/lib/util";
import {
  createEngagements,
  sendBulkMessage,
  transitionState,
  useEngagements,
  useInstagramRecipients,
  useSmsTemplates,
  type OutreachChannel,
} from "@/services/engagement";
import SmsTemplateManager from "@/components/Modals/SmsTemplateManager";
import EngagementChatDialog from "@/components/Modals/EngagementChatDialog";
import useUser from "@/lib/useUser";
import { isMetaReviewEmail } from "@/lib/constants";
import TransitionStateConfirmDialog from "./TransitionStateConfirmDialog";
import type { TransitionStateBody } from "@/services/engagement";
import type { KeyedMutator } from "swr";
import { toast } from "sonner";

const STATE_COLORS: Record<string, "yellow" | "blue" | "orange" | "cyan" | "green" | "red" | "gray"> = {
  Pending: "yellow",
  Drafting: "blue",
  Redrafting: "orange",
  WaitingToPost: "cyan",
  Posted: "green",
  Declined: "red",
  Canceled: "gray",
};

const STATE_LABELS: Record<string, string> = {
  Pending: "Pending",
  Drafting: "Drafting",
  Redrafting: "Redrafting",
  WaitingToPost: "Waiting to Post",
  Posted: "Posted",
  Declined: "Declined",
  Canceled: "Canceled",
};

const TRANSITION_ACTIONS: Record<string, string> = {
  Pending: "Restart Outreach",
  Drafting: "Mark as Drafting",
  Redrafting: "Request Revision",
  WaitingToPost: "Approve Draft",
  Posted: "Confirm Posted",
  Declined: "Mark Declined",
  Canceled: "Cancel Outreach",
};

const VALID_TRANSITIONS: Record<string, string[]> = {
  Pending: ["Drafting", "Declined", "Canceled"],
  Drafting: ["WaitingToPost", "Redrafting", "Canceled"],
  Redrafting: ["WaitingToPost", "Canceled"],
  WaitingToPost: ["Posted", "Canceled", "Drafting", "Redrafting"],
  Posted: [],
  Declined: [],
  Canceled: ["Pending"],
};

const DROP_STATES = [
  "Pending",
  "Drafting",
  "Redrafting",
  "WaitingToPost",
  "Posted",
  "Declined",
  "Canceled",
] as const;

function dropId(state: string) {
  return `drop-${state}`;
}

/** Prefer pointer position over rect overlap so columns register drops when dragging over cards. */
const kanbanCollisionDetection: CollisionDetection = (args) => {
  const pointerHits = pointerWithin(args);
  if (pointerHits.length > 0) return pointerHits;
  return rectIntersection(args);
};

function platformIcon(platform: string) {
  const p = platform?.toLowerCase();
  if (p === "instagram") return <IconBrandInstagram size={14} />;
  if (p === "tiktok") return <IconBrandTiktok size={14} />;
  if (p === "twitter") return <IconBrandX size={14} />;
  if (p === "youtube") return <IconBrandYoutube size={14} />;
  return null;
}

type EngagementRow = {
  id: string;
  offerToken?: string;
  state: string;
  creatorPriceId: number;
  customPrice?: number | null;
  outreachChannel?: OutreachChannel;
  recipientIgScopedId?: string | null;
  /** Latest row only (server `orderBy: desc, take: 1`) */
  messages?: Array<{ direction?: string }>;
  creatorPrice: {
    username?: string;
    platform?: string;
    price?: number;
    creator: { id: number; name?: string; smsOptIn?: boolean; avatar?: string };
  };
};

/** True when the most recent SMS in the thread is inbound (creator replied last). */
function latestMessageIsFromCreator(eng: EngagementRow): boolean {
  const last = eng.messages?.[0];
  return last?.direction === "inbound";
}

function KanbanColumn({
  state,
  title,
  count,
  children,
  droppable,
}: {
  state: string;
  title: string;
  count: number;
  children: React.ReactNode;
  droppable: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: dropId(state),
    disabled: !droppable,
    data: { state },
  });
  const badgeColor = STATE_COLORS[state as keyof typeof STATE_COLORS] || "gray";

  return (
    <Flex
      ref={setNodeRef}
      direction="column"
      gap="2"
      style={{
        flex: "1 1 0",
        minWidth: 180,
        maxWidth: "100%",
        borderRadius: "var(--radius-3)",
        border: `1px solid ${isOver && droppable ? "var(--accent-8)" : "var(--gray-a5)"}`,
        background: isOver && droppable ? "var(--accent-a2)" : "var(--gray-a2)",
        padding: "var(--space-2)",
        minHeight: 200,
      }}
    >
      <Flex align="center" justify="between" gap="2" px="1" py="1">
        <Text size="2" weight="bold">
          {title}
        </Text>
        <Badge size="1" variant="soft" color={badgeColor}>
          {count}
        </Badge>
      </Flex>
      <Flex direction="column" gap="2" style={{ flex: 1 }}>
        {children}
      </Flex>
    </Flex>
  );
}

function NotStartedColumn({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <Flex
      direction="column"
      gap="2"
      style={{
        flex: "1 1 0",
        minWidth: 180,
        maxWidth: "100%",
        borderRadius: "var(--radius-3)",
        border: "1px solid var(--gray-a5)",
        background: "var(--gray-a2)",
        padding: "var(--space-2)",
        minHeight: 200,
      }}
    >
      <Flex align="center" justify="between" gap="2" px="1" py="1">
        <Text size="2" weight="bold">
          {title}
        </Text>
        <Badge size="1" variant="soft" color="gray">
          {count}
        </Badge>
      </Flex>
      <Flex direction="column" gap="2" style={{ flex: 1 }}>
        {children}
      </Flex>
    </Flex>
  );
}

function EngagementCard({
  engagement,
  price,
  onTransitionClick,
  onOpenChat,
}: {
  engagement: EngagementRow;
  price: number;
  onTransitionClick: (toState: string) => void;
  onOpenChat: () => void;
}) {
  const cp = engagement.creatorPrice;
  const creator = cp.creator;
  // Posted and Declined are truly terminal in the UI; Canceled stays interactive
  // so users can restart outreach via drag-to-Pending or the menu.
  const terminal = engagement.state === "Posted" || engagement.state === "Declined";
  const transitions = VALID_TRANSITIONS[engagement.state] || [];

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `eng-${engagement.id}`,
    disabled: terminal,
    data: { kind: "engagement" as const, engagement },
  });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.45 : 1,
  };

  const smsPhone = getCreatorSmsPhone(creator as any);
  const hasPhone = creatorHasSmsPhone(creator as any);
  const creatorRepliedLast = latestMessageIsFromCreator(engagement);

  return (
    <Box ref={setNodeRef} style={style}>
      <Card size="1">
      <Flex direction="column" gap="2">
        <Flex align="start" gap="2">
          {!terminal && (
            <Box
              {...listeners}
              {...attributes}
              style={{ cursor: "grab", touchAction: "none", paddingTop: 2 }}
              aria-label="Drag to change state"
            >
              <IconGripVertical size={18} color="var(--gray-9)" />
            </Box>
          )}
          <Avatar
            size="2"
            src={cp.creator?.avatar ? `/image-upload/${cp.creator.avatar}` : undefined}
            fallback={(creator?.name || "?")[0]}
            radius="full"
          />
          <Flex direction="column" gap="1" style={{ minWidth: 0, flex: 1 }}>
            <Flex align="center" gap="2" wrap="nowrap" style={{ minWidth: 0 }}>
              <Link href={`/creators/${creator?.id}`} style={{ fontWeight: 600, fontSize: 13, minWidth: 0 }}>
                {creator?.name}
              </Link>
              {creatorRepliedLast && (
                <Box
                  title="Latest message is from the creator — open chat to reply"
                  style={{ flexShrink: 0, lineHeight: 0 }}
                  aria-label="Latest message is from the creator"
                >
                  <IconMessageCircle size={18} color="var(--green-11)" stroke={2} />
                </Box>
              )}
            </Flex>
            <Flex align="center" gap="1" wrap="wrap">
              {platformIcon(cp.platform || "")}
              <Text size="1" color="gray" style={{ minWidth: 0 }}>
                @{cp.username} · ${Number(price).toLocaleString()} · {capitalize(cp.platform || "")}
              </Text>
            </Flex>
            {smsPhone && (
              <Text size="1" color="gray">
                {smsPhone}
              </Text>
            )}
            <Badge size="1" variant="soft" color={STATE_COLORS[engagement.state] || "gray"}>
              {STATE_LABELS[engagement.state] || engagement.state}
            </Badge>
            <Badge
              size="1"
              variant="outline"
              color={engagement.outreachChannel === "Instagram" ? "purple" : "blue"}
            >
              {engagement.outreachChannel === "Instagram" ? "IG DM" : "SMS"}
            </Badge>
            {!hasPhone && engagement.outreachChannel !== "Instagram" && (
              <Badge size="1" color="orange" variant="outline">
                No phone
              </Badge>
            )}
          </Flex>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <IconButton size="1" variant="ghost">
                <IconDotsVertical size={16} />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end" size="1">
              <DropdownMenu.Item onSelect={onOpenChat}>
                <IconMessage size={14} /> Open chat
              </DropdownMenu.Item>
              {!terminal && transitions.length > 0 && <DropdownMenu.Separator />}
              {!terminal &&
                transitions.map((s) => (
                  <DropdownMenu.Item
                    key={s}
                    color={s === "Canceled" || s === "Declined" ? "red" : undefined}
                    onSelect={() => onTransitionClick(s)}
                  >
                    {TRANSITION_ACTIONS[s]}
                  </DropdownMenu.Item>
                ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Flex>
      </Flex>
      </Card>
    </Box>
  );
}

function NotStartedCard({
  budget,
  price,
  onStart,
}: {
  budget: any;
  price: number;
  onStart: () => void;
}) {
  const cp = budget.creatorPrice;
  const creator = cp.creator;
  return (
    <Card size="1">
      <Flex direction="column" gap="2">
        <Flex align="start" gap="2">
          <Avatar
            size="2"
            src={cp.creator?.avatar ? `/image-upload/${cp.creator.avatar}` : undefined}
            fallback={(creator?.name || "?")[0]}
            radius="full"
          />
          <Flex direction="column" gap="1" style={{ minWidth: 0, flex: 1 }}>
            <Link href={`/creators/${creator?.id}`} style={{ fontWeight: 600, fontSize: 13 }}>
              {creator?.name}
            </Link>
            <Flex align="center" gap="1" wrap="wrap">
              {platformIcon(cp.platform)}
              <Text size="1" color="gray">
                @{cp.username} · ${Number(price).toLocaleString()} · {capitalize(cp.platform || "")}
              </Text>
            </Flex>
            <Badge size="1" variant="soft" color="gray">
              Not started
            </Badge>
          </Flex>
        </Flex>
        <Button size="2" variant="solid" onClick={onStart}>
          Start outreach
        </Button>
      </Flex>
    </Card>
  );
}

export default function OutreachKanban({
  campaign,
  mutate,
}: {
  campaign: Campaign;
  mutate: KeyedMutator<Campaign>;
}) {
  const { user } = useUser();
  // Meta App Review account: hide bulk outreach so the demo stays 1:1.
  const isReviewer = isMetaReviewEmail(user?.email);
  const { data: engagements, mutate: mutateEngagements } = useEngagements(campaign.id);
  const { data: templates } = useSmsTemplates(true);
  const engList = useMemo(
    () => (Array.isArray(engagements) ? (engagements as EngagementRow[]) : []),
    [engagements]
  );

  const engagementByPriceId = useMemo(() => {
    const m: Record<number, EngagementRow> = {};
    engList.forEach((e) => {
      m[e.creatorPriceId] = e;
    });
    return m;
  }, [engList]);

  const budgets = campaign.budgets || [];
  const notStartedBudgets = useMemo(
    () => budgets.filter((b: any) => b.creatorPrice?.id && !engagementByPriceId[b.creatorPrice.id]),
    [budgets, engagementByPriceId]
  );

  const summary = useMemo(
    () => calculateEngagementBudget(campaign, engList as any[]),
    [campaign, engList]
  );

  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [showTerminal, setShowTerminal] = useState(false);
  /** When many budget lines lack engagement, collapse "Not started" to save horizontal space. */
  const [showNotStartedExpanded, setShowNotStartedExpanded] = useState(false);
  const [showBulkMessage, setShowBulkMessage] = useState(false);
  const [bulkBody, setBulkBody] = useState("");
  const [bulkTemplateId, setBulkTemplateId] = useState("");
  const [sending, setSending] = useState(false);

  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [startPriceIds, setStartPriceIds] = useState<number[]>([]);
  const [startCustomPrices, setStartCustomPrices] = useState<Record<number, string>>({});
  const [startingAll, setStartingAll] = useState(false);
  const [startChannel, setStartChannel] = useState<OutreachChannel>("Sms");
  const [startIgAccountId, setStartIgAccountId] = useState<string>("");
  const [startIgConversationId, setStartIgConversationId] = useState<string>("");

  const singleStartPriceId = startPriceIds.length === 1 ? startPriceIds[0] : undefined;
  const { data: startIgRecipients } = useInstagramRecipients(
    singleStartPriceId,
    showStartConfirm && startChannel === "Instagram" && !!singleStartPriceId,
  );

  const [pendingTransition, setPendingTransition] = useState<{
    engagementId: string;
    newState: string;
    creatorName: string;
    username: string;
    hasPhone: boolean;
    smsOptIn: boolean;
  } | null>(null);
  const [transitioning, setTransitioning] = useState(false);

  const [dragEngagement, setDragEngagement] = useState<EngagementRow | null>(null);

  const [chatEngId, setChatEngId] = useState<string | null>(null);

  const [dropTransition, setDropTransition] = useState<{
    engagementId: string;
    newState: string;
    creatorName: string;
    username: string;
    hasPhone: boolean;
    smsOptIn: boolean;
  } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const filterEngagement = useCallback(
    (e: EngagementRow) => {
      if (platformFilter !== "all") {
        const p = e.creatorPrice?.platform?.toLowerCase();
        if (p !== platformFilter.toLowerCase()) return false;
      }
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const name = e.creatorPrice?.creator?.name?.toLowerCase() || "";
      const un = e.creatorPrice?.username?.toLowerCase() || "";
      return name.includes(q) || un.includes(q);
    },
    [search, platformFilter]
  );

  const filterBudget = useCallback(
    (b: any) => {
      if (platformFilter !== "all") {
        const p = b.creatorPrice?.platform?.toLowerCase();
        if (p !== platformFilter.toLowerCase()) return false;
      }
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const name = b.creatorPrice?.creator?.name?.toLowerCase() || "";
      const un = b.creatorPrice?.username?.toLowerCase() || "";
      return name.includes(q) || un.includes(q);
    },
    [search, platformFilter]
  );

  const byState = useMemo(() => {
    const m: Record<string, EngagementRow[]> = {};
    DROP_STATES.forEach((s) => {
      m[s] = [];
    });
    engList.filter(filterEngagement).forEach((e) => {
      if (m[e.state]) m[e.state].push(e);
    });
    return m;
  }, [engList, filterEngagement]);

  const notStartedFiltered = useMemo(
    () => notStartedBudgets.filter(filterBudget),
    [notStartedBudgets, filterBudget]
  );

  const terminalEngagements = useMemo(
    () => engList.filter((e) => e.state === "Declined" || e.state === "Canceled").filter(filterEngagement),
    [engList, filterEngagement]
  );

  const engagementCount = engList.length;

  const collapseNotStarted = notStartedFiltered.length > 8;
  const showNotStartedColumn = !collapseNotStarted || showNotStartedExpanded;

  const openStartConfirm = (priceIds: number[]) => {
    const u = Array.from(new Set(priceIds.filter(Boolean)));
    if (!u.length) return;
    const init: Record<number, string> = {};
    for (const pid of u) {
      const b = budgets.find(
        (x: any) => x.creatorPrice?.id === pid || x.creatorPriceId === pid
      );
      const p = b?.customCreatorPrice ?? b?.creatorPrice?.price ?? 0;
      init[pid] = String(p);
    }
    setStartCustomPrices(init);
    setStartPriceIds(u);
    setStartChannel("Sms");
    setStartIgAccountId("");
    setStartIgConversationId("");
    setShowStartConfirm(true);
  };

  const budgetsForStart = budgets.filter(
    (b: any) => b.creatorPrice?.id && startPriceIds.includes(b.creatorPrice.id)
  );
  const withPhoneCount = budgetsForStart.filter((b: any) =>
    creatorHasSmsPhone(b.creatorPrice?.creator)
  ).length;
  const pendingTemplate = (templates || []).find(
    (t: any) => t.triggerState === "Pending" && t.enabled
  );

  const igMatch =
    startIgRecipients?.matches?.find((m) => m.id === startIgConversationId) ??
    startIgRecipients?.matches?.[0];
  const igAccount =
    startIgRecipients?.accounts?.find((a) => a.id === startIgAccountId) ??
    startIgRecipients?.accounts?.find((a) => a.id === igMatch?.igAccountId) ??
    startIgRecipients?.accounts?.[0];
  const canStartInstagram =
    startChannel === "Instagram" &&
    startPriceIds.length === 1 &&
    !!igMatch &&
    !!igAccount;

  const handleStartOutreach = async () => {
    if (!startPriceIds.length) return;
    if (startChannel === "Instagram" && !canStartInstagram) {
      toast.error("Instagram outreach requires an existing DM thread with this creator");
      return;
    }
    setStartingAll(true);
    try {
      const customPrices: Record<number, number> = {};
      for (const pid of startPriceIds) {
        const raw = (startCustomPrices[pid] ?? "").trim().replace(/[^0-9.]/g, "");
        const n = parseFloat(raw);
        if (Number.isFinite(n)) customPrices[pid] = Math.round(n);
      }
      await createEngagements(campaign.id, startPriceIds, {
        customPrices: Object.keys(customPrices).length ? customPrices : undefined,
        outreachChannel: startChannel,
        ...(startChannel === "Instagram" && igMatch && igAccount
          ? {
              instagramAccountId: igAccount.id,
              recipientIgScopedId: igMatch.participantIgScopedId,
              instagramConversationId: igMatch.id,
            }
          : {}),
      });
      await mutateEngagements();
      setShowStartConfirm(false);
      setStartPriceIds([]);
      mutate();
    } catch (e) {
      console.error(e);
      toast.error("Failed to start outreach");
    } finally {
      setStartingAll(false);
    }
  };

  const handleBulkSend = async () => {
    setSending(true);
    try {
      const useTemplate = bulkTemplateId && bulkTemplateId !== "__custom__";
      await sendBulkMessage({
        campaignId: campaign.id,
        ...(useTemplate ? { templateId: bulkTemplateId } : { customBody: bulkBody }),
      });
      setShowBulkMessage(false);
      setBulkBody("");
      setBulkTemplateId("");
      await mutateEngagements();
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const handleTransitionConfirm = async (
    smsOptions: Pick<TransitionStateBody, "sendStateSms" | "stateSmsBody"> | undefined
  ) => {
    const p = pendingTransition || dropTransition;
    if (!p) return;
    setTransitioning(true);
    try {
      await transitionState(p.engagementId, p.newState, smsOptions);
      setPendingTransition(null);
      setDropTransition(null);
      await mutateEngagements();
      mutate();
    } finally {
      setTransitioning(false);
    }
  };

  const onDragStart = (e: DragStartEvent) => {
    const d = e.active.data.current as { kind?: string; engagement?: EngagementRow } | undefined;
    if (d?.kind === "engagement" && d.engagement) setDragEngagement(d.engagement);
  };

  const onDragEnd = (e: DragEndEvent) => {
    setDragEngagement(null);
    const { active, over } = e;
    const data = active.data.current as { kind?: string; engagement?: EngagementRow } | undefined;
    if (data?.kind !== "engagement" || !data.engagement) return;
    const eng = data.engagement;
    if (!over) return;
    const overId = String(over.id);
    if (!overId.startsWith("drop-")) return;
    const toState = overId.replace("drop-", "");
    if (eng.state === toState) return;
    if (!VALID_TRANSITIONS[eng.state]?.includes(toState)) {
      toast.error("This move is not allowed", {
        description: `Cannot go from ${STATE_LABELS[eng.state] || eng.state} to ${STATE_LABELS[toState] || toState}.`,
      });
      return;
    }
    const cp = eng.creatorPrice;
    setDropTransition({
      engagementId: eng.id,
      newState: toState,
      creatorName: cp.creator?.name || "",
      username: cp.username || "",
      hasPhone: creatorHasSmsPhone(cp.creator as any),
      smsOptIn: !!cp.creator?.smsOptIn,
    });
  };

  const openTransitionFromMenu = (eng: EngagementRow, newState: string) => {
    const cp = eng.creatorPrice;
    setPendingTransition({
      engagementId: eng.id,
      newState,
      creatorName: cp.creator?.name || "",
      username: cp.username || "",
      hasPhone: creatorHasSmsPhone(cp.creator as any),
      smsOptIn: !!cp.creator?.smsOptIn,
    });
  };

  const transitionDialog = pendingTransition || dropTransition;
  const transitionEngagement = useMemo(() => {
    const d = pendingTransition || dropTransition;
    if (!d) return null;
    return engList.find((e) => e.id === d.engagementId) ?? null;
  }, [pendingTransition, dropTransition, engList]);

  return (
    <Flex direction="column" gap="4">
      <Flex gap="3" wrap="wrap" style={{ width: "100%" }}>
        <Card size="2" style={{ flex: "1 1 min-content", minWidth: "min(100%, 140px)" }}>
          <Text size="1" color="gray">
            Total budget
          </Text>
          <Heading size="5">${summary.totalBudget.toLocaleString()}</Heading>
        </Card>
        <Card size="2" style={{ flex: "1 1 min-content", minWidth: "min(100%, 140px)" }}>
          <Text size="1" color="gray">
            Committed
          </Text>
          <Heading size="5">${summary.committed.toLocaleString()}</Heading>
        </Card>
        <Card size="2" style={{ flex: "1 1 min-content", minWidth: "min(100%, 140px)" }}>
          <Text size="1" color="gray">
            Spent (posted)
          </Text>
          <Heading size="5">${summary.spent.toLocaleString()}</Heading>
        </Card>
        <Card size="2" style={{ flex: "1 1 min-content", minWidth: "min(100%, 140px)" }}>
          <Text size="1" color="gray">
            Available
          </Text>
          <Heading size="5">${summary.available.toLocaleString()}</Heading>
        </Card>
      </Flex>

      <Flex direction="row" gap="2" align="center" wrap="wrap" justify="between">
        <Flex gap="2" wrap="wrap" align="center" style={{ flex: 1 }}>
          <TextField.Root
            placeholder="Search creators…"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
            style={{ minWidth: 200, maxWidth: 320 }}
          >
            <TextField.Slot>
              <IconSearch size={16} />
            </TextField.Slot>
          </TextField.Root>
          <Select.Root value={platformFilter} onValueChange={setPlatformFilter}>
            <Select.Trigger placeholder="Platform" />
            <Select.Content>
              <Select.Item value="all">All platforms</Select.Item>
              <Select.Item value="instagram">Instagram</Select.Item>
              <Select.Item value="tiktok">TikTok</Select.Item>
              <Select.Item value="twitter">Twitter</Select.Item>
              <Select.Item value="youtube">YouTube</Select.Item>
            </Select.Content>
          </Select.Root>
        </Flex>
        <Flex gap="2" wrap="wrap" align="center">
          {notStartedBudgets.length > 0 && (
            <Button
              size="2"
              variant="solid"
              onClick={() =>
                openStartConfirm(
                  notStartedBudgets.map((b: any) => b.creatorPrice?.id).filter(Boolean)
                )
              }
            >
              Start all ({notStartedBudgets.length})
            </Button>
          )}
          {!isReviewer && engagementCount > 0 && (
            <Button size="2" variant="soft" onClick={() => setShowBulkMessage(true)}>
              <IconSend size={14} /> Bulk outreach
            </Button>
          )}
          <SmsTemplateManager />
        </Flex>
      </Flex>

      <DndContext
        sensors={sensors}
        collisionDetection={kanbanCollisionDetection}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <Flex direction="column" gap="3" style={{ width: "100%" }}>
          <Flex
            gap="3"
            style={{
              overflowX: "hidden",
              width: "100%",
              paddingBottom: "var(--space-2)",
              flexWrap: "nowrap",
              alignItems: "stretch",
            }}
          >
            {collapseNotStarted && !showNotStartedColumn ? (
              <Card
                size="2"
                style={{
                  flex: "0 0 auto",
                  minWidth: 160,
                  maxWidth: 200,
                  alignSelf: "stretch",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Text size="2" weight="bold">
                  Not started
                </Text>
                <Badge size="2" variant="soft" color="gray" mt="2">
                  {notStartedFiltered.length} lines
                </Badge>
                <Button size="2" variant="soft" mt="3" onClick={() => setShowNotStartedExpanded(true)}>
                  Show
                </Button>
              </Card>
            ) : (
              <NotStartedColumn title="Not started" count={notStartedFiltered.length}>
                {collapseNotStarted && showNotStartedColumn && (
                  <Button size="1" variant="ghost" mb="1" onClick={() => setShowNotStartedExpanded(false)}>
                    Collapse
                  </Button>
                )}
                {notStartedFiltered.map((b: any) => (
                  <NotStartedCard
                    key={b.id}
                    budget={b}
                    price={b.customCreatorPrice ?? b.creatorPrice?.price ?? 0}
                    onStart={() => openStartConfirm([b.creatorPrice.id])}
                  />
                ))}
              </NotStartedColumn>
            )}

            {(["Pending", "Drafting", "Redrafting", "WaitingToPost", "Posted"] as const).map((state) => (
              <KanbanColumn
                key={state}
                state={state}
                title={STATE_LABELS[state]}
                count={byState[state]?.length ?? 0}
                droppable
              >
                {(byState[state] || []).map((eng) => (
                  <EngagementCard
                    key={eng.id}
                    engagement={eng}
                    price={budgetLinePrice(campaign, eng.creatorPriceId, eng.customPrice)}
                    onTransitionClick={(to) => openTransitionFromMenu(eng, to)}
                    onOpenChat={() => setChatEngId(eng.id)}
                  />
                ))}
              </KanbanColumn>
            ))}
          </Flex>

          <Flex direction="column" gap="2" style={{ width: "100%" }}>
            <Button variant="soft" size="1" style={{ alignSelf: "flex-start" }} onClick={() => setShowTerminal((s) => !s)}>
              {showTerminal ? "Hide" : "Show"} declined / canceled ({terminalEngagements.length})
            </Button>
            {showTerminal && (
              <Flex
                gap="3"
                style={{
                  width: "100%",
                  flexWrap: "wrap",
                  alignItems: "stretch",
                }}
              >
                <KanbanColumn state="Declined" title="Declined" count={byState.Declined?.length ?? 0} droppable>
                  {(byState.Declined || []).map((eng) => (
                    <EngagementCard
                      key={eng.id}
                      engagement={eng}
                      price={budgetLinePrice(campaign, eng.creatorPriceId, eng.customPrice)}
                      onTransitionClick={() => {}}
                      onOpenChat={() => setChatEngId(eng.id)}
                    />
                  ))}
                </KanbanColumn>
                <KanbanColumn state="Canceled" title="Canceled" count={byState.Canceled?.length ?? 0} droppable>
                  {(byState.Canceled || []).map((eng) => (
                    <EngagementCard
                      key={eng.id}
                      engagement={eng}
                      price={budgetLinePrice(campaign, eng.creatorPriceId, eng.customPrice)}
                      onTransitionClick={(to) => openTransitionFromMenu(eng, to)}
                      onOpenChat={() => setChatEngId(eng.id)}
                    />
                  ))}
                </KanbanColumn>
              </Flex>
            )}
          </Flex>
        </Flex>

        <DragOverlay dropAnimation={null}>
          {dragEngagement ? (
            <Card size="1" style={{ width: 260, opacity: 0.95 }}>
              <Text size="2" weight="bold">
                {dragEngagement.creatorPrice.creator?.name}
              </Text>
              <Text size="1" color="gray">
                @{dragEngagement.creatorPrice.username}
              </Text>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>

      <EngagementChatDialog
        open={!!chatEngId}
        engagementId={chatEngId}
        campaign={campaign}
        onOpenChange={(o) => {
          if (!o) setChatEngId(null);
        }}
        onSent={() => void mutateEngagements()}
      />

      {/* Start outreach confirm */}
      <Dialog.Root
        open={showStartConfirm}
        onOpenChange={(o) => {
          setShowStartConfirm(o);
          if (!o) {
            setStartPriceIds([]);
            setStartCustomPrices({});
            setStartChannel("Sms");
          }
        }}
      >
        <Dialog.Content maxWidth="540px">
          <Dialog.Title>Confirm start outreach</Dialog.Title>
          <Dialog.Description size="2" color="gray" mb="3">
            Create engagement(s) for {startPriceIds.length} budget line(s).
          </Dialog.Description>
          <Text size="2" weight="medium" mb="2">
            Channel
          </Text>
          <Flex gap="2" mb="3" wrap="wrap">
            <Button
              type="button"
              variant={startChannel === "Sms" ? "solid" : "soft"}
              onClick={() => {
                setStartChannel("Sms");
                setStartIgAccountId("");
                setStartIgConversationId("");
              }}
            >
              SMS / iMessage
            </Button>
            <Button
              type="button"
              variant={startChannel === "Instagram" ? "solid" : "soft"}
              color="purple"
              disabled={startPriceIds.length !== 1}
              title={
                startPriceIds.length !== 1
                  ? "Instagram outreach is available when starting one creator at a time"
                  : !startIgRecipients?.matches?.length
                    ? "Creator must DM your IG account first"
                    : undefined
              }
              onClick={() => setStartChannel("Instagram")}
            >
              Instagram DM
            </Button>
          </Flex>
          {startChannel === "Instagram" && startPriceIds.length === 1 && (
            <Box mb="3" p="3" style={{ background: "var(--gray-a3)", borderRadius: "var(--radius-2)" }}>
              {!startIgRecipients?.accounts?.length ? (
                <Text size="2" color="orange">
                  Connect an Instagram business account in{" "}
                  <Link href="/me" style={{ textDecoration: "underline" }}>
                    Settings
                  </Link>{" "}
                  (shared across your agency) before using IG outreach.
                </Text>
              ) : !startIgRecipients.matches.length ? (
                <Text size="2" color="orange">
                  No DM thread found for @{startIgRecipients.creatorUsername}. The creator must
                  message your IG account first.
                </Text>
              ) : (
                <Flex direction="column" gap="2">
                  <Text size="1" color="gray">
                    Meta limits cold outreach to 1 tagged message per creator per 7 days. Once they
                    reply, follow-ups use the standard 24h window.
                  </Text>
                  {startIgRecipients.accounts.length > 1 && (
                    <>
                      <Text size="2" weight="medium">
                        Send from account
                      </Text>
                      <Select.Root
                        value={startIgAccountId || igAccount?.id || ""}
                        onValueChange={setStartIgAccountId}
                      >
                        <Select.Trigger placeholder="Select account…" />
                        <Select.Content>
                          {startIgRecipients.accounts.map((a) => (
                            <Select.Item key={a.id} value={a.id}>
                              @{a.igUsername || a.igUserId}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    </>
                  )}
                  {startIgRecipients.matches.length > 1 && (
                    <>
                      <Text size="2" weight="medium">
                        Conversation
                      </Text>
                      <Select.Root
                        value={startIgConversationId || igMatch?.id || ""}
                        onValueChange={setStartIgConversationId}
                      >
                        <Select.Trigger placeholder="Select conversation…" />
                        <Select.Content>
                          {startIgRecipients.matches.map((m) => (
                            <Select.Item key={m.id} value={m.id}>
                              @{m.participantUsername || m.participantIgScopedId}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    </>
                  )}
                  {startIgRecipients.matches.length === 1 && (
                    <Text size="2">
                      Thread: @{igMatch?.participantUsername || startIgRecipients.creatorUsername}
                    </Text>
                  )}
                </Flex>
              )}
            </Box>
          )}
          <Card size="1" mb="3">
            <Flex direction="column" gap="2">
              <Flex justify="between">
                <Text size="2">With phone (SMS)</Text>
                <Text size="2" weight="bold">
                  {withPhoneCount}
                </Text>
              </Flex>
              <Flex justify="between">
                <Text size="2">No phone</Text>
                <Text size="2" color="gray">
                  {startPriceIds.length - withPhoneCount}
                </Text>
              </Flex>
            </Flex>
          </Card>
          {(startChannel === "Sms" ? withPhoneCount > 0 : canStartInstagram) && pendingTemplate && (
            <Box p="3" mb="3" style={{ background: "var(--gray-a3)", borderRadius: "var(--radius-2)" }}>
              <Text size="2" mb="1" color="gray">
                Initial message preview ({startChannel === "Instagram" ? "Instagram" : "SMS"})
              </Text>
              <Text size="2">{pendingTemplate.body}</Text>
            </Box>
          )}
          <Text size="2" weight="medium" mb="2">
            Offer rate (USD) per line
          </Text>
          <Flex direction="column" gap="2" mb="3" style={{ maxHeight: 220, overflowY: "auto" }}>
            {startPriceIds.map((pid) => {
              const b = budgets.find(
                (x: any) => x.creatorPrice?.id === pid || x.creatorPriceId === pid
              );
              const name = b?.creatorPrice?.creator?.name ?? "Creator";
              return (
                <Flex key={pid} align="center" gap="2" justify="between" wrap="wrap">
                  <Text size="2" style={{ flex: "1 1 120px", minWidth: 0 }}>
                    {name}
                  </Text>
                  <TextField.Root
                    size="2"
                    style={{ width: 120 }}
                    value={startCustomPrices[pid] ?? ""}
                    onChange={(ev) =>
                      setStartCustomPrices((s) => ({ ...s, [pid]: ev.target.value }))
                    }
                  >
                    <TextField.Slot side="left">$</TextField.Slot>
                  </TextField.Root>
                </Flex>
              );
            })}
          </Flex>
          <Flex gap="2" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              onClick={handleStartOutreach}
              disabled={startingAll || (startChannel === "Instagram" && !canStartInstagram)}
            >
              {startingAll ? "Starting…" : "Confirm & start"}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Bulk SMS */}
      <Dialog.Root open={showBulkMessage} onOpenChange={setShowBulkMessage}>
        <Dialog.Content maxWidth="520px">
          <Dialog.Title>Bulk outreach</Dialog.Title>
          <Dialog.Description size="2" color="gray" mb="3">
            Send to each engagement on its configured channel (SMS or Instagram). Eligible
            creators only ({engagementCount} engagements).
          </Dialog.Description>
          <Flex direction="column" gap="3">
            <Select.Root
              value={bulkTemplateId}
              onValueChange={(v) => {
                setBulkTemplateId(v);
                setBulkBody("");
              }}
            >
              <Select.Trigger placeholder="Template…" />
              <Select.Content>
                <Select.Item value="__custom__">Custom message</Select.Item>
                {(templates || []).map((t: any) => (
                  <Select.Item key={t.id} value={t.id}>
                    {t.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
            {(!bulkTemplateId || bulkTemplateId === "__custom__") && (
              <TextArea
                rows={4}
                value={bulkBody}
                onChange={(ev) => setBulkBody(ev.target.value)}
                placeholder="Hi {{creatorName}}! …"
              />
            )}
            <Flex gap="2" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                onClick={handleBulkSend}
                disabled={
                  sending ||
                  ((!bulkTemplateId || bulkTemplateId === "__custom__") && !bulkBody.trim())
                }
              >
                {sending ? "Sending…" : "Send"}
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Transition confirm (menu + drag) */}
      {transitionDialog && (
        <TransitionStateConfirmDialog
          key={`${transitionDialog.engagementId}-${transitionDialog.newState}`}
          open
          onOpenChange={(o) => {
            if (!o) {
              setPendingTransition(null);
              setDropTransition(null);
            }
          }}
          title={TRANSITION_ACTIONS[transitionDialog.newState]}
          stateLabel={STATE_LABELS[transitionDialog.newState] || transitionDialog.newState}
          creatorName={transitionDialog.creatorName}
          username={transitionDialog.username}
          hasPhone={transitionDialog.hasPhone}
          smsOptIn={transitionDialog.smsOptIn}
          newState={transitionDialog.newState}
          campaign={campaign}
          engagement={transitionEngagement}
          templates={templates}
          transitioning={transitioning}
          onConfirm={handleTransitionConfirm}
        />
      )}

    </Flex>
  );
}
